import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineCalendarIcon from 'components/Luxkit/Icons/line/LineCalendarIcon'
import LineClockWithTickIcon from 'components/Luxkit/Icons/line/LineClockWithTickIcon'
import LineCommentsAltIcon from 'components/Luxkit/Icons/line/LineCommentsAltIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import React from 'react'
import styled, { keyframes } from 'styled-components'
import eases from 'styles/tools/eases'

const ItemTransition = keyframes`
  0% {
    transform: translateX(0);
  }

  28% {
    transform: translateX(0);
  }

  /* Transition to slide 2 */

  33% {
    transform: translateX(calc(-100% - ${rem(18)}));
  }

  61% {
    transform: translateX(calc(-100% - ${rem(18)}));
  }
  /* Transition to slide 3 */

  66% {
    transform: translateX(calc(-200% - ${rem(36)}));
  }

  94% {
    transform: translateX(calc(-200% - ${rem(36)}));
  }

  /* Transition to slide 1 */

  100% {
    transform: translateX(0);
  }
`

const BubbleTransition = keyframes`
  0% {
    width: ${rem(16)};
  }

  28% {
    width: ${rem(16)};
  }

  33% {
    width: ${rem(8)};
  }

  94% {
    width: ${rem(8)};
  }

  100% {
    width: ${rem(16)};
  }
`

const BubbleProgressTransition = keyframes`
  0% {
    transform: translateX(-100%);
    opacity: 1;
  }

  /* Slide across the bubble */

  28% {
    transform: translateX(0%);
    opacity: 1;
  }

  /* Fade out as we change slides */

  33% {
    transform: translateX(0%);
    opacity: 0;
  }

  /* Wait for our turn again */

  100% {
    transform: translateX(-100%);
    opacity: 0;
  }
`

const CarouselBubbles = styled.div`
  display: flex;
  gap: ${rem(4)};

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const CarouselBubble = styled.div`
  position: relative;
  height: ${rem(8)};
  width: ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.default.four};
  animation: ${BubbleTransition} 9s infinite;
  overflow: hidden;
  transform: translateZ(0);


  &:nth-child(2) {
    animation-delay: 3s;

    &::after {
      animation-delay: 3s;
    }
  }

  &:nth-child(3) {
    animation-delay: 6s;

    &::after {
      animation-delay: 6s;
    }
  }

  &::after {
    display: block;
    content: '';
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    transform: translateX(-100%);
    background-color: ${props => props.theme.palette.neutral.default.seven};
    animation: ${BubbleProgressTransition} 9s infinite;
  }
`

const CruiseUSPs = styled.div`
  display: grid;
  gap: ${rem(16)};
  overflow: visible;
  margin-top: ${rem(24)};

  ${mediaQueryUp.tablet} {
    margin-top: ${rem(36)};
    animation: none;
    grid-template-columns: unset;
    grid-auto-flow: column;
    justify-content: space-between;
  }

  &.carousel {
    animation: ${ItemTransition} 9s infinite ${eases.power2.inOut};
    grid-template-columns: repeat(3, 100%);

    ${mediaQueryUp.tablet} {
      animation: none;
      grid-template-columns: unset;
    }
  }
`

const CruiseUSP = styled.div`
  display: flex;
  flex-direction: column;

  > * + * {
    margin-top: ${rem(12)};
  }

  ${mediaQueryUp.tablet} {
    width: ${rem(360)};
  }
`

const AnimationContainer = styled(VerticalSpacer)`
  overflow: hidden;
  margin-left: -${rem(16)};
  margin-right: -${rem(16)};
  padding-left: ${rem(16)};
  padding-right: ${rem(16)};

  > ${mediaQueryUp.tablet} {
    margin-left: 0;
    margin-right: 0;
    padding-left: 0;
    padding-right: 0;
  }

  &.paused {
    ${CruiseUSPs} {
      animation-play-state: paused;
    }

    ${CarouselBubble} {
      /* Indicate a paused state by fading it a little */
      opacity: 0.6;
      transform: scale(0.8);
      animation-play-state: paused;

      &::after {
        animation-play-state: paused;
      }
    }
  }
`

function CruiseVerticalUSPs() {
  return <AnimationContainer gap={20} >
    <CruiseUSPs className="carousel">
      <CruiseUSP>
        <LineCalendarIcon size="L" colour="neutral-one"/>
        <VerticalSpacer gap={4}>
          <BodyText colour="neutral-one" variant="medium" weight="bold">Thousands of handpicked itineraries</BodyText>
          <BodyText variant="medium" colour="neutral-two">Explore the world on the greatest cruise lines</BodyText>
        </VerticalSpacer>
      </CruiseUSP>

      <CruiseUSP>
        <LineClockWithTickIcon size="L" colour="neutral-one"/>
        <VerticalSpacer gap={4}>
          <BodyText colour="neutral-one" variant="medium" weight="bold">Instant booking confirmation</BodyText>
          <BodyText variant="medium" colour="neutral-two">Real-time availability for immediate cruise confirmation</BodyText>
        </VerticalSpacer>
      </CruiseUSP>

      <CruiseUSP>
        <LineCommentsAltIcon size="L" colour="neutral-one"/>
        <VerticalSpacer gap={4}>
          <BodyText colour="neutral-one" variant="medium" weight="bold">24/7 Support service</BodyText>
          <BodyText variant="medium" colour="neutral-two">Round-the-clock support from our dedicated team</BodyText>
        </VerticalSpacer>
      </CruiseUSP>
    </CruiseUSPs>

    <CarouselBubbles>
      <CarouselBubble />
      <CarouselBubble />
      <CarouselBubble />
    </CarouselBubbles>
  </AnimationContainer>
}

export default CruiseVerticalUSPs
